import React from 'react';
import type { ShortcutDefinition, ShortcutScope } from './keymap';
import { GLOBAL_SCOPE } from './keymap';
import {
    isFunctionKeyCombination,
    isIgnorableKeydown,
    matchesCombination,
} from './matching';
import { isEditableTarget } from './isEditableTarget';

export type ShortcutBinding = {
    handle: (event: KeyboardEvent) => void;
    enabled: boolean;
    onUnavailable?: () => void;
};

type ShortcutContextValue = {
    registerBinding: (id: string, binding: ShortcutBinding) => void;
    unregisterBinding: (id: string) => void;
    pushScope: (scope: string) => void;
    popScope: (scope: string) => void;
    /** The scope stack's current top (`global` when nothing is pushed), read imperatively — e.g. by the cheat sheet, to know what to list first. */
    getActiveScope: () => ShortcutScope;
};

const ShortcutContext = React.createContext<ShortcutContextValue | null>(null);

/** The provider's whole context, for this module's own hooks and the cheat sheet; features use the narrow hooks the barrel exports instead. */
export const useShortcutContext = (): ShortcutContextValue => {
    const context = React.useContext(ShortcutContext);
    if (!context) {
        throw new Error(
            'Shortcut hooks must be used within a ShortcutProvider',
        );
    }
    return context;
};

type ShortcutProviderProps = {
    /** The keymap this provider matches against — `shortcutRegistry` in the app, a fixture in tests. */
    registry: ShortcutDefinition[];
    children: React.ReactNode;
};

/**
 * Mounted once at the app root (see `App.tsx`). Owns the registry's
 * `keydown` listeners (function keys in the capture phase, every other key
 * bubbling) and the scope stack: a mounted page pushes its base
 * scope (`useShortcutScope`), a dialog pushes its own on top while it is
 * open, and only the top scope plus `global` are live — everything below is
 * suspended until the dialog unmounts and pops it. Bindings register through
 * `useShortcut`, which looks itself up in `registry` by id; a `displayOnly`
 * entry never matches here. The keys those entries describe are read by
 * feature listeners of their own (`usePinPhysicalKeys`,
 * `usePaymentPhysicalKeys`, `useShiftDialogKeys`) or by the design system,
 * and the feature listeners gate themselves on the same stack through
 * `useIsShortcutScopeActive`.
 */
export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({
    registry,
    children,
}) => {
    const bindingsRef = React.useRef(new Map<string, ShortcutBinding>());
    const scopeStackRef = React.useRef<string[]>([]);

    const registryById = React.useMemo(
        () =>
            new Map(registry.map((definition) => [definition.id, definition])),
        [registry],
    );

    const registerBinding = React.useCallback(
        (id: string, binding: ShortcutBinding) => {
            bindingsRef.current.set(id, binding);
        },
        [],
    );
    const unregisterBinding = React.useCallback((id: string) => {
        bindingsRef.current.delete(id);
    }, []);
    const pushScope = React.useCallback((scope: string) => {
        scopeStackRef.current = [...scopeStackRef.current, scope];
    }, []);
    const popScope = React.useCallback((scope: string) => {
        const stack = scopeStackRef.current;
        const index = stack.lastIndexOf(scope);
        if (index === -1) {
            return;
        }
        scopeStackRef.current = [
            ...stack.slice(0, index),
            ...stack.slice(index + 1),
        ];
    }, []);
    const getActiveScope = React.useCallback((): ShortcutScope => {
        const stack = scopeStackRef.current;
        return stack[stack.length - 1] ?? GLOBAL_SCOPE;
    }, []);

    React.useEffect(() => {
        // Function keys are matched in the capture phase on `window`, ahead
        // of every other listener, and a handled one goes no further: React
        // Aria's landmark manager takes `F6` in the document's capture phase
        // whenever a landmark is on screen (a toast region is one), so a
        // bubbling listener never saw the line-discount key while a toast
        // showed (QA #100). Every other key stays in the bubble phase, so a
        // focused control's own handling (a button's `Enter`) still wins.
        const handleKeyDown = (event: KeyboardEvent, functionKeys: boolean) => {
            if (isIgnorableKeydown(event)) {
                return;
            }

            const stack = scopeStackRef.current;
            const topScope = stack[stack.length - 1];
            const liveScopes = new Set(
                topScope === undefined
                    ? [GLOBAL_SCOPE]
                    : [GLOBAL_SCOPE, topScope],
            );

            // Registration order breaks a tie between two live bindings that
            // match the same event; the registry guard keeps that from
            // happening within one scope, so this only matters across scopes.
            for (const [id, binding] of bindingsRef.current) {
                const definition = registryById.get(id);
                if (
                    !definition ||
                    definition.displayOnly ||
                    !liveScopes.has(definition.scope)
                ) {
                    continue;
                }
                const isFunctionKey = isFunctionKeyCombination(
                    definition.combination,
                );
                if (
                    isFunctionKey !== functionKeys ||
                    !matchesCombination(event, definition.combination)
                ) {
                    continue;
                }
                if (
                    !isFunctionKey &&
                    !definition.firesWhileEditable &&
                    isEditableTarget(event.target)
                ) {
                    continue;
                }

                if (binding.enabled) {
                    binding.handle(event);
                } else if (binding.onUnavailable) {
                    binding.onUnavailable();
                } else {
                    continue;
                }
                event.preventDefault();
                if (isFunctionKey) {
                    event.stopPropagation();
                }
                return;
            }
        };
        const handleFunctionKey = (event: KeyboardEvent) =>
            handleKeyDown(event, true);
        const handleOtherKey = (event: KeyboardEvent) =>
            handleKeyDown(event, false);

        window.addEventListener('keydown', handleFunctionKey, true);
        window.addEventListener('keydown', handleOtherKey);
        return () => {
            window.removeEventListener('keydown', handleFunctionKey, true);
            window.removeEventListener('keydown', handleOtherKey);
        };
    }, [registryById]);

    const value = React.useMemo<ShortcutContextValue>(
        () => ({
            registerBinding,
            unregisterBinding,
            pushScope,
            popScope,
            getActiveScope,
        }),
        [
            registerBinding,
            unregisterBinding,
            pushScope,
            popScope,
            getActiveScope,
        ],
    );

    return (
        <ShortcutContext.Provider value={value}>
            {children}
        </ShortcutContext.Provider>
    );
};
